import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { modelFormSchema, type ModelFormData } from '@geely/shared/schemas';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Switch,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@geely/ui";
import type { ModelWithVersions } from '@geely/shared';

interface ModelFormDialogProps {
  isOpen: boolean;
  editingModel: ModelWithVersions | null;
  onSubmit: (data: ModelFormData) => Promise<void>;
  onClose: () => void;
}

const defaultValues: ModelFormData = {
  name: '',
  isActive: true,
};

export function ModelFormDialog({
  isOpen,
  editingModel,
  onSubmit,
  onClose,
}: ModelFormDialogProps) {
  const form = useForm<ModelFormData>({
    resolver: zodResolver(modelFormSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const { formState: { isSubmitting } } = form;

  useEffect(() => {
    if (isOpen) {
      if (editingModel) {
        form.reset({
          name: editingModel.name,
          isActive: editingModel.isActive,
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [isOpen, editingModel, form]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingModel ? 'Editar Modelo' : 'Novo Modelo'}
          </DialogTitle>
          <DialogDescription>
            Preencha os dados do modelo
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ex: Coolray" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Ativo</FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Carregando...' : (editingModel ? 'Atualizar' : 'Criar')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
